import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { customersAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Plus, MagnifyingGlass, Eye, PencilSimple, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';

const CustomersPage = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    account_number: '',
    company_name: '',
    trading_name: '',
    vat_number: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchCustomers();
  }, [search]);

  const fetchCustomers = async () => {
    try {
      const response = await customersAPI.list({ search, limit: 50 });
      setCustomers(response.data);
    } catch (error) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      await customersAPI.create(newCustomer);
      toast.success('Customer created successfully');
      setIsCreateOpen(false);
      setNewCustomer({ account_number: '', company_name: '', trading_name: '', vat_number: '' });
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create customer');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;
    try {
      await customersAPI.delete(id);
      toast.success('Customer deleted');
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete customer');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="customers-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold">Customers</h1>
          <p className="text-muted-foreground">Manage your customer accounts and sites</p>
        </div>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-orange-500 hover:bg-orange-400 text-white font-bold" data-testid="create-customer-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Customer
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Customer</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label>Account Number *</Label>
                <Input
                  value={newCustomer.account_number}
                  onChange={(e) => setNewCustomer({ ...newCustomer, account_number: e.target.value })}
                  placeholder="e.g., CUST001"
                  data-testid="customer-account-number"
                />
              </div>
              <div className="space-y-2">
                <Label>Company Name *</Label>
                <Input
                  value={newCustomer.company_name}
                  onChange={(e) => setNewCustomer({ ...newCustomer, company_name: e.target.value })}
                  placeholder="Company Ltd"
                  data-testid="customer-company-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Trading Name</Label>
                <Input
                  value={newCustomer.trading_name}
                  onChange={(e) => setNewCustomer({ ...newCustomer, trading_name: e.target.value })}
                  placeholder="Trading as..."
                />
              </div>
              <div className="space-y-2">
                <Label>VAT Number</Label>
                <Input
                  value={newCustomer.vat_number}
                  onChange={(e) => setNewCustomer({ ...newCustomer, vat_number: e.target.value })}
                  placeholder="GB123456789"
                />
              </div>
              <Button onClick={handleCreate} className="w-full bg-orange-500 hover:bg-orange-400 text-white font-bold" data-testid="save-customer-btn">
                Create Customer
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search customers..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="search-customers"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full" />
            </div>
          ) : customers.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No customers found. Create your first customer to get started.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Account #</TableHead>
                  <TableHead>Company Name</TableHead>
                  <TableHead>Sites</TableHead>
                  <TableHead>Credit Limit</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {customers.map((customer) => (
                  <TableRow key={customer.id} data-testid={`customer-row-${customer.id}`}>
                    <TableCell className="font-mono text-sm">{customer.account_number}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{customer.company_name}</p>
                        {customer.trading_name && (
                          <p className="text-xs text-muted-foreground">t/a {customer.trading_name}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{customer.sites?.length || 0}</TableCell>
                    <TableCell className="font-mono">
                      £{(customer.credit?.credit_limit || 0).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {customer.credit?.is_on_stop ? (
                        <Badge variant="destructive">On Stop</Badge>
                      ) : customer.is_active ? (
                        <Badge className="status-success">Active</Badge>
                      ) : (
                        <Badge variant="secondary">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => navigate(`/customers/${customer.id}`)}
                          data-testid={`view-customer-${customer.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(customer.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CustomersPage;
